import { useEffect, useRef } from 'react';
import { Application } from 'pixi.js';
import { Box } from '@mui/material';

const GameTable = ({
    onMount,
    backgroundColor = 0x1099bb
}) => {
    const canvasRef = useRef(null);
    const appRef = useRef(null);

    useEffect(() => {
        let app;
        let mounted = true;
        let resizeObserver;

        const initPixi = async () => {
            try {
                if (!canvasRef.current) return;

                // Get the actual dimensions of the container
                let containerWidth = canvasRef.current.clientWidth;
                let containerHeight = canvasRef.current.clientHeight;

                // If the container has 0 height (can happen at mount), wait a few frames
                // until it receives a non-zero height (usually after layout). This avoids
                // creating a PIXI canvas with height 0.
                if (containerHeight === 0) {
                    let attempts = 0;
                    const maxAttempts = 60; // ~1 second at 60fps
                    while (containerHeight === 0 && attempts < maxAttempts) {
                        // wait for next animation frame
                        // eslint-disable-next-line no-await-in-loop
                        await new Promise((res) => requestAnimationFrame(res));
                        attempts += 1;
                        if (!canvasRef.current) break;
                        containerWidth = canvasRef.current.clientWidth;
                        containerHeight = canvasRef.current.clientHeight;
                    }
                }

                // Fallback if still zero: use window innerHeight so canvas is visible
                if (containerHeight === 0) {
                    containerHeight = window.innerHeight || 600;
                }

                app = new Application();

                await app.init({
                    width: containerWidth,
                    height: containerHeight,
                    backgroundColor,
                    resolution: window.devicePixelRatio || 1,
                    autoDensity: true,
                });

                // Ensure the canvas fills its container via CSS so it always uses 100% width/height
                const view = app.canvas || app.view || app.renderer?.view;
                if (view) {
                    view.style.width = '100%';
                    view.style.height = '100%';
                    view.style.display = 'block';
                }

                if (!mounted) {
                    if (app.renderer) {
                        app.destroy(true, { children: true, texture: true, baseTexture: true });
                    }
                    return;
                }

                appRef.current = app;

                if (canvasRef.current) {
                    canvasRef.current.appendChild(app.canvas);
                }

                // Enable PixiJS DevTools (only in development)
                if (typeof window !== 'undefined') {
                    window.__PIXI_DEVTOOLS__ = { app };
                }

                if (onMount) {
                    onMount(app);
                }

                // Handle resize
                let resizeScheduled = false;
                let pendingWidth = 0;
                let pendingHeight = 0;

                resizeObserver = new ResizeObserver((entries) => {
                    // Use the first entry's contentRect if available, fallback to container client sizes
                    const entry = entries && entries[0];
                    const cw = entry && entry.contentRect && entry.contentRect.width
                        ? Math.round(entry.contentRect.width)
                        : (canvasRef.current ? canvasRef.current.clientWidth : 0);
                    const ch = entry && entry.contentRect && entry.contentRect.height
                        ? Math.round(entry.contentRect.height)
                        : (canvasRef.current ? canvasRef.current.clientHeight : 0);

                    pendingWidth = cw;
                    pendingHeight = ch;

                    if (!resizeScheduled) {
                        resizeScheduled = true;
                        requestAnimationFrame(() => {
                            resizeScheduled = false;
                            if (canvasRef.current && app.renderer) {
                                // Only resize if dimensions actually changed to avoid layout loops
                                if (app.renderer.width !== pendingWidth || app.renderer.height !== pendingHeight) {
                                    try {
                                        app.renderer.resize(pendingWidth, pendingHeight);
                                    } catch (e) {
                                        // swallow errors to avoid dev-overlay spam; still log for debugging
                                        // but don't throw
                                        // console.warn('Resize failed:', e);
                                    }
                                }
                            }
                        });
                    }
                });

                if (canvasRef.current) resizeObserver.observe(canvasRef.current);

            } catch (error) {
                console.error('Error initializing PixiJS in GameTable:', error);
            }
        };

        initPixi();

        return () => {
            mounted = false;
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
            if (app) {
                if (app.renderer) {
                    app.destroy(true, { children: true, texture: true, baseTexture: true });
                }
            }
        };
    }, [backgroundColor]); // Removed onMount from dependencies

    return (
        <Box
            ref={canvasRef}
            sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
            }}
        />
    );
};

export default GameTable;
